import moment from 'moment'
import { getScheduleChangeLegPairs } from 'lib/flights/getScheduleChangeLegPairs'

export function parseScheduleChangeDateTime(value: string) {
  // Snapshots contain airport-local wall clocks, not UTC instants.
  return moment.utc(value, 'DD/MM/YYYY-HH:mm', true)
}

export function getScheduleChangeDisplay(change: App.FlightScheduleChange | undefined, legs: Array<Pick<App.Leg, 'id'>>, now: number) {
  if (!change || !['awaiting_decision', 'accepted', 'auto_accepted', 'declined'].includes(change.status)) return undefined

  const showComparison = change.status === 'awaiting_decision' || change.status === 'declined'
  // Pending decisions with no usable deadline retain the existing booking display.
  if (showComparison && (!change.respondBy || !(Date.parse(change.respondBy) > now))) return undefined

  const pairs = getScheduleChangeLegPairs(change).flatMap(({ oldLeg, newLeg }) => {
    if (!oldLeg || !newLeg) return []
    const bookingLeg = legs.find(leg => leg.id === newLeg.id || leg.id === oldLeg.id)
    if (!bookingLeg) return []
    const valid = [oldLeg, newLeg].every(leg => leg.flights.length > 0 &&
      [leg, ...leg.flights].every(flight => Boolean(flight.departureAirport && flight.arrivalAirport) && parseScheduleChangeDateTime(flight.departureDateTime).isValid() &&
        parseScheduleChangeDateTime(flight.arrivalDateTime).isValid()))
    return valid ? [{ bookingLegId: bookingLeg.id, oldLeg, newLeg }] : []
  })

  // Never show a partial or incorrectly matched change as the complete proposal.
  if (!pairs.length || pairs.length !== change.affectedLegs.length) return undefined
  return { change, pairs, showComparison }
}

export type ScheduleChangeLegPair = NonNullable<ReturnType<typeof getScheduleChangeDisplay>>['pairs'][number]

export type ScheduleChangeRebookingDisplay = 'in_progress' | 'manual_review' | 'confirmed'

export function getScheduleChangeRebookingDisplay(change: App.FlightScheduleChange | undefined): ScheduleChangeRebookingDisplay | undefined {
  if (!change) return undefined
  const status = change.rebooking?.status
  if (change.status === 'rebooked' || status === 'queued') return 'confirmed'
  if (status === 'manual_review') return 'manual_review'
  if (status === 'booking' || status === 'booked' || status === 'queueing') return 'in_progress'
  return undefined
}

export interface ScheduleChangeActionAvailability {
  accept: boolean
  requestSupport: boolean
  selectAlternative: boolean
}

const NO_SCHEDULE_CHANGE_ACTIONS: ScheduleChangeActionAvailability = { accept: false, requestSupport: false, selectAlternative: false }

export function getScheduleChangeActionAvailability(change: App.FlightScheduleChange | undefined): ScheduleChangeActionAvailability {
  if (!change || !['awaiting_decision', 'declined'].includes(change.status) || getScheduleChangeRebookingDisplay(change) || !change.availableActions) return NO_SCHEDULE_CHANGE_ACTIONS
  return {
    accept: change.availableActions.includes('ACCEPT'),
    requestSupport: change.availableActions.includes('REQUEST_SUPPORT'),
    selectAlternative: change.availableActions.includes('SELECT_ALTERNATIVE'),
  }
}

export function canPerformScheduleChangeAction(change: App.FlightScheduleChange | undefined, action: 'ACCEPT' | 'REQUEST_SUPPORT') {
  const availability = getScheduleChangeActionAvailability(change)
  return action === 'ACCEPT' ? availability.accept : availability.requestSupport
}
